import { useEffect, useState, useLayoutEffect } from "react";
import "./BrandNav.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { ChevronDown, Search } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import ApiService from "../../components/Api_services";
import FallbackImg from "../FallbackImg";

const BrandNav = ({ brandList = [], clearInput, setClearInput, disabled }) => {
  const [brands, setBrands] = useState([]);
  const [searchBrand, setSearchBrand] = useState("");
  const [openIndex, setOpenIndex] = useState(null);
  const [animationState, setAnimationState] = useState("");
  const [selectedBrands, setSelectedBrands] = useState([]);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const brandsFromUrl = searchParams.get("brands");
    setSelectedBrands(brandsFromUrl ? brandsFromUrl.split(",") : []);
  }, [searchParams]);

  // Auto-collapse brands dropdown when a product is selected
  useEffect(() => {
    const productFromUrl = searchParams.get("product");
    if (productFromUrl && openIndex === 0) {
      setAnimationState("closing");
      setTimeout(() => {
        setOpenIndex(null);
        setAnimationState("");
      }, 300);
    }
  }, [searchParams.get("product")]);

  const filteredBrandGroups = brandList.filter((group) =>
    group.brand_name.toLowerCase().includes(searchBrand.toLowerCase()),
  );

  const handleToggle = (index) => {
    if (disabled) return;

    if (openIndex === index) {
      // Closing - now allowed even when brands are selected
      setAnimationState("closing");
      setTimeout(() => {
        setOpenIndex(null);
        setAnimationState("");
      }, 300); // Match CSS transition duration
    } else {
      // Opening
      setOpenIndex(index);
      setAnimationState("opening");
      setTimeout(() => {
        setAnimationState("");
      }, 300);
    }
  };

  const handleCheckboxChange = (brand) => {
    setSelectedBrands((prevSelected) => {
      let newSelected;
      if (prevSelected.includes(brand)) {
        // Remove brand if already selected
        newSelected = prevSelected.filter((b) => b !== brand);
      } else {
        // Add brand if not selected
        newSelected = [...prevSelected, brand];
      }

      // Update URL parameters
      const newSearchParams = new URLSearchParams(searchParams);
      if (newSelected.length > 0) {
        newSearchParams.set("brands", newSelected.join(","));
        newSearchParams.set("search", "1");
        // 移除 filter_type 参数
        //newSearchParams.delete("filter_type");
      } else {
        newSearchParams.delete("brands");
        // 移除 filter_type 参数
        //newSearchParams.delete("filter_type");
      }
      setSearchParams(newSearchParams);

      return newSelected;
    });

    // Auto-collapse only when no brands are selected
    setTimeout(() => {
      setSelectedBrands((currentSelected) => {
        if (currentSelected.length === 0 && openIndex === 0) {
          setAnimationState("closing");
          setTimeout(() => {
            setOpenIndex(null);
            setAnimationState("");
          }, 300);
        }
        return currentSelected;
      });
    }, 0);
  };

  const handleClearAll = () => {
    setSelectedBrands([]);

    // Update URL parameters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("brands");
    setSearchParams(newSearchParams);

    // Auto-collapse dropdown
    if (openIndex === 0) {
      setAnimationState("closing");
      setTimeout(() => {
        setOpenIndex(null);
        setAnimationState("");
      }, 300);
    }
  };

  useLayoutEffect(() => {
    const brandFromState = location.state?.selectedBrand || null;
    if (brandFromState && !disabled) {
      setOpenIndex(0);
      setSelectedBrands([brandFromState]);
    }
  }, [location.state]);

  // Clear search input if clearInput is true or auto-open dropdown
  useEffect(() => {
    if (clearInput === "openDropdowns") {
      if (!disabled) {
        // Auto-open brands dropdown
        setOpenIndex(0);
        setAnimationState("opening");
        setTimeout(() => {
          setAnimationState("");
        }, 300);
      }
      // Don't reset clearInput here - let CategoryNav handle it
    } else if (clearInput) {
      setSearchBrand("");
      if (typeof setClearInput === "function") setClearInput(false);
    }
  }, [clearInput, setClearInput]);

  // force-closes the dropdown whenever disabled becomes true
  useEffect(() => {
    if (disabled && openIndex !== null) {
      setAnimationState("closing");
      setTimeout(() => {
        setOpenIndex(null);
        setAnimationState("");
      }, 300);
    }
  }, [disabled]);

  return (
    <div className="brandnav_v2">
      <div className="brandnav-content_v2">
        <div className="brandnav-dropdown_v2">
          <div className="branditem-container_v2">
            <div
              className={`branditem_v2 ${disabled ? " disabled" : ""}`}
              onClick={() => handleToggle(0)}
            >
              <div style={{ display: "flex", gap: "10px" }}>
                <strong>BRANDS</strong>
                {selectedBrands.length > 0 && (
                  <span
                    style={{
                      color: "#666",
                      fontSize: "0.9em",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    (
                    {selectedBrands.length === 1
                      ? "1"
                      : `${selectedBrands.length}`}
                    )
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearAll();
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#999",
                        cursor: "pointer",
                        padding: "0",
                        fontSize: "12px",
                        lineHeight: "1",
                        borderRadius: "50%",
                        width: "16px",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      title="Clear all selections"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
              <ChevronDown
                size={16}
                className={openIndex === 0 ? "rotate" : ""}
              />
            </div>

            {openIndex === 0 && (
              <div className={`brand-dropdown-wrapper_v2 ${animationState}`}>
                <div className="searchbar2-container_v2">
                  <div className="searchbar2_v2">
                    <input
                      type="text"
                      placeholder="Search brands..."
                      value={searchBrand}
                      onChange={(e) => setSearchBrand(e.target.value)}
                    />
                    <Search size={16} />
                  </div>
                </div>

                <ul className="subbrandlist_v2">
                  {filteredBrandGroups.map((brand, index) => (
                    <li key={index} className="subbranditem_v2">
                      <input
                        type="checkbox"
                        id={`brand-${brand.brand_id}`}
                        name="brand"
                        value={brand.brand_id}
                        checked={selectedBrands.includes(
                          brand.brand_id.toString(),
                        )}
                        onChange={() =>
                          handleCheckboxChange(brand.brand_id.toString())
                        }
                      />
                      <label
                        htmlFor={`brand-${brand.brand_id}`}
                        style={{ cursor: "pointer" }}
                      >
                        {brand.brand_name}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandNav;
